import React, { createContext, useContext, useState, useEffect } from "react";
import { isAuthenticated as checkIsAuthenticated } from "../utils/isAuthenticated";

interface AuthContextType {
  isAuthenticated: boolean;
  login: (token: string, refreshToken?: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  // Function to check authentication status using the improved utility
  const checkAuthentication = () => {
    setIsAuthenticated(checkIsAuthenticated());
  };

  // Check authentication status on component mount
  useEffect(() => {
    checkAuthentication();
  }, []);

  // Check authentication periodically to handle token expiration
  useEffect(() => {
    const intervalId = setInterval(checkAuthentication, 60 * 1000); // Check every minute
    return () => clearInterval(intervalId);
  }, []);

  const login = (token: string, refreshToken?: string) => {
    console.log("Attempting to login with token");
    localStorage.setItem("token", token);
    if (refreshToken) {
      localStorage.setItem("refreshToken", refreshToken);
    }
    checkAuthentication(); // Ensure the state is updated after login
  };

  const logout = () => {
    console.log("Logging out");
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    checkAuthentication();
    // Redirect to login page
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
